import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SavedFoodsQuickLog from "@/components/SavedFoodsQuickLog";
import MacroRings from "@/components/MacroRings";

const ACTIVITY = [
  { id: "sedentary", label: "Sedentary", hint: "Desk job, no exercise" },
  { id: "light", label: "Light", hint: "1–3 workouts / week" },
  { id: "moderate", label: "Moderate", hint: "3–5 workouts / week" },
  { id: "active", label: "Active", hint: "6–7 workouts / week" },
  { id: "very_active", label: "Very active", hint: "Twice a day / physical job" },
];

const GOAL = [
  { id: "lose_aggressive", kgPerWeek: -0.75, hint: "Aggressive deficit", emoji: "🔥" },
  { id: "lose_steady",     kgPerWeek: -0.5,  hint: "Recommended",       emoji: "🎯" },
  { id: "lose_mild",       kgPerWeek: -0.25, hint: "Gentle",            emoji: "🌱" },
  { id: "maintain",        kgPerWeek: 0,     hint: "Stay where you are", emoji: "⚖️" },
  { id: "gain",            kgPerWeek: 0.25,  hint: "Slow muscle build",  emoji: "💪" },
  { id: "custom",          kgPerWeek: null,  hint: "Type your own",     emoji: "✏️" },
];

// UI uses kg or lb (UK convention is lb-per-week even for stones users — stones-per-week is too coarse).
function goalLabel(g, unit) {
  if (g.id === "maintain") return "Maintain weight";
  if (g.id === "gain") return unit === "kg" ? "Lean gain · +0.25 kg / week" : "Lean gain · +0.55 lb / week";
  if (g.id === "custom") return "Custom · pick your own pace";
  const amt = Math.abs(g.kgPerWeek);
  const verb = g.kgPerWeek < 0 ? "Lose" : "Gain";
  if (unit === "kg") return `${verb} ${amt} kg / week`;
  const lb = Math.round(amt * 2.20462 * 100) / 100; // lb to 2dp
  return `${verb} ${lb} lb / week`;
}

function weeklyChangeLabel(kgChange, unit) {
  const sign = kgChange > 0 ? "+" : "";
  if (unit === "kg") return `${sign}${kgChange} kg / week`;
  const lb = Math.round(kgChange * 2.20462 * 100) / 100;
  return `${sign}${lb} lb / week`;
}

// --- Unit conversions ---
const cmToFtIn = (cm) => {
  if (cm == null || cm === "" || isNaN(cm)) return { ft: "", inches: "" };
  const totalInches = Number(cm) / 2.54;
  let ft = Math.floor(totalInches / 12);
  let inches = Math.round((totalInches - ft * 12) * 10) / 10;
  if (inches === 12) { ft += 1; inches = 0; }
  return { ft, inches };
};
const ftInToCm = (ft, inches) => {
  const f = Number(ft) || 0;
  const i = Number(inches) || 0;
  return Math.round(((f * 12) + i) * 2.54 * 10) / 10;
};
const kgToLb = (kg) => Math.round(Number(kg) * 2.20462 * 10) / 10;
const lbToKg = (lb) => Math.round(Number(lb) / 2.20462 * 10) / 10;
const kgToStLb = (kg) => {
  if (!kg) return { st: "", lb: "" };
  const totalLb = Number(kg) * 2.20462;
  let st = Math.floor(totalLb / 14);
  let lb = Math.round((totalLb - st * 14) * 10) / 10;
  if (lb >= 14) { st += 1; lb -= 14; }
  return { st, lb };
};
const stLbToKg = (st, lb) => {
  const s = Number(st) || 0;
  const p = Number(lb) || 0;
  return Math.round(((s * 14) + p) / 2.20462 * 10) / 10;
};

// Default values: kg + cm (most accurate)
const DEFAULT_FORM = {
  age: 30, sex: "female", height_cm: 165, weight_kg: 68,
  activity: "light", goal: "lose_steady", custom_kg_per_week: -0.4,
};

export default function CalorieTarget() {
  // Numeric metric values (what the API expects). Allow "" for "blank".
  const [form, setForm] = useState(DEFAULT_FORM);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  // Unit selections (UI-only, doesn't go to API)
  const [heightUnit, setHeightUnit] = useState("cm"); // "cm" | "ft"
  const [weightUnit, setWeightUnit] = useState("kg"); // "kg" | "lb" | "st"

  // Free-form raw inputs (strings) so the user can clear them without seeing "0".
  const [hCm, setHCm] = useState("165");
  const [hFt, setHFt] = useState("");
  const [hIn, setHIn] = useState("");
  const [wKg, setWKg] = useState("68");
  const [wLb, setWLb] = useState("");
  const [wSt, setWSt] = useState("");
  const [wStLb, setWStLb] = useState("");
  const [ageStr, setAgeStr] = useState("30");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/calorie-target");
        if (data.calorie_profile) {
          const p = data.calorie_profile;
          setForm({ ...DEFAULT_FORM, ...p });
          setAgeStr(String(p.age ?? ""));
          setHCm(String(p.height_cm ?? ""));
          setWKg(String(p.weight_kg ?? ""));
          // initialise ft/in + st/lb mirrors from saved values
          const { ft, inches } = cmToFtIn(p.height_cm);
          setHFt(String(ft));
          setHIn(String(inches));
          setWLb(String(kgToLb(p.weight_kg)));
          const { st, lb } = kgToStLb(p.weight_kg);
          setWSt(String(st));
          setWStLb(String(lb));
          // restore the user's last-used unit pickers
          if (p.height_unit === "cm" || p.height_unit === "ft") setHeightUnit(p.height_unit);
          if (p.weight_unit === "kg" || p.weight_unit === "lb" || p.weight_unit === "st") setWeightUnit(p.weight_unit);
          setSaved(data.result);
          setResult(data.result);
        }
      } catch { /* first time */ }
    })();
  }, []);

  // When unit toggles, sync the input strings from the canonical metric value.
  const switchHeight = (next) => {
    setHeightUnit(next);
    if (next === "cm") {
      setHCm(form.height_cm ? String(form.height_cm) : "");
    } else {
      const { ft, inches } = cmToFtIn(form.height_cm || 0);
      setHFt(form.height_cm ? String(ft) : "");
      setHIn(form.height_cm ? String(inches) : "");
    }
  };
  const switchWeight = (next) => {
    setWeightUnit(next);
    if (next === "kg") {
      setWKg(form.weight_kg ? String(form.weight_kg) : "");
    } else if (next === "lb") {
      setWLb(form.weight_kg ? String(kgToLb(form.weight_kg)) : "");
    } else {
      const { st, lb } = kgToStLb(form.weight_kg || 0);
      setWSt(form.weight_kg ? String(st) : "");
      setWStLb(form.weight_kg ? String(lb) : "");
    }
  };

  // Live-sync from raw inputs → canonical metric form
  const updateHeight = () => {
    let cm = null;
    if (heightUnit === "cm") cm = hCm === "" ? null : Number(hCm);
    else cm = (hFt === "" && hIn === "") ? null : ftInToCm(hFt, hIn);
    setForm((f) => ({ ...f, height_cm: cm }));
  };
  const updateWeight = () => {
    let kg = null;
    if (weightUnit === "kg") kg = wKg === "" ? null : Number(wKg);
    else if (weightUnit === "lb") kg = wLb === "" ? null : lbToKg(wLb);
    else kg = (wSt === "" && wStLb === "") ? null : stLbToKg(wSt, wStLb);
    setForm((f) => ({ ...f, weight_kg: kg }));
  };
  useEffect(updateHeight, [hCm, hFt, hIn, heightUnit]); // eslint-disable-line
  useEffect(updateWeight, [wKg, wLb, wSt, wStLb, weightUnit]); // eslint-disable-line
  useEffect(() => {
    setForm((f) => ({ ...f, age: ageStr === "" ? null : Number(ageStr) }));
  }, [ageStr]);

  const validate = () => {
    if (!form.age || form.age < 10 || form.age > 120) return "Enter a realistic age (10–120)";
    if (!form.height_cm || form.height_cm < 80 || form.height_cm > 260) return "Enter a realistic height";
    if (!form.weight_kg || form.weight_kg < 25 || form.weight_kg > 400) return "Enter a realistic weight";
    return null;
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const calculate = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    try {
      const { data } = await api.post("/me/calorie-target/calculate", form);
      setResult(data);
    } catch {
      toast.error("Could not calculate — check your inputs");
    }
  };

  const saveTarget = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = { ...form, height_unit: heightUnit, weight_unit: weightUnit };
      const { data } = await api.put("/me/calorie-target", payload);
      setSaved(data.result);
      setResult(data.result);
      toast.success(`Target saved · ${data.result.daily_kcal} kcal / day`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 ff-fade-in">
      <header>
        <div className="ff-label">Daily target</div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Your calorie sweet-spot
        </h1>
        <p className="text-[#5C6B61] mt-2 max-w-xl">
          Tell us a bit about you and we&apos;ll work out how many calories you should eat each day to hit your goal — using the trusted Mifflin–St Jeor formula.
        </p>
      </header>

      {saved && (
        <div className="ff-card p-6 md:p-8 bg-gradient-to-br from-[#E8F0E9] to-[#F3F0EA]" data-testid="saved-target-card">
          <div className="ff-label text-[#3B5A3E]">Your current target</div>
          <div className="flex items-end gap-3 mt-2">
            <span className="text-5xl sm:text-6xl font-semibold text-[#3B5A3E]" style={{ fontFamily: "Outfit, sans-serif" }}>
              {saved.daily_kcal}
            </span>
            <span className="text-lg text-[#5C6B61] pb-2">kcal / day</span>
          </div>
          <p className="text-sm text-[#5C6B61] mt-2">
            Maintenance is ~{saved.tdee} kcal · BMR {saved.bmr} kcal. Projected change: <strong>{weeklyChangeLabel(saved.weekly_kg_change, weightUnit)}</strong>.
          </p>
        </div>
      )}

      {saved && <MacroRings />}

      <SavedFoodsQuickLog
        defaultOpen={false}
        title="Quick-log a food to today"
        emptyHint="Scan a food first, hit ☆ Save, then log it from here in one tap."
        testidPrefix="calories-saved-foods"
      />

      <div className="grid lg:grid-cols-[1fr_0.9fr] gap-8">
        <section className="ff-card p-6 md:p-8 space-y-6" data-testid="calorie-form">
          <Field label="Age">
            <input
              type="number"
              min="10"
              max="120"
              data-testid="age-input"
              className="ff-input"
              value={ageStr}
              onChange={(e) => setAgeStr(e.target.value)}
              placeholder="30"
            />
          </Field>

          <Field label="Sex">
            <div className="flex gap-2">
              {[["female", "Female"], ["male", "Male"]].map(([id, label]) => (
                <button
                  key={id}
                  data-testid={`sex-${id}`}
                  onClick={() => set("sex", id)}
                  className={`flex-1 py-2.5 rounded-full text-sm font-medium border transition ${form.sex === id ? "bg-[#3B5A3E] text-white border-[#3B5A3E]" : "bg-white text-[#2A332C] border-[#E5E2DA] hover:border-[#3B5A3E]"}`}
                >{label}</button>
              ))}
            </div>
          </Field>

          {/* Height with unit toggle */}
          <Field
            label="Height"
            unitToggle={
              <UnitToggle
                value={heightUnit}
                options={[{ id: "cm", label: "cm" }, { id: "ft", label: "ft / in" }]}
                onChange={switchHeight}
                testid="height-unit"
              />
            }
          >
            {heightUnit === "cm" ? (
              <input
                type="number"
                min="80"
                max="260"
                step="0.5"
                data-testid="height-input"
                className="ff-input"
                value={hCm}
                onChange={(e) => setHCm(e.target.value)}
                placeholder="165"
              />
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <input
                    type="number"
                    min="2"
                    max="8"
                    data-testid="height-ft-input"
                    className="ff-input pr-8"
                    value={hFt}
                    onChange={(e) => setHFt(e.target.value)}
                    placeholder="5"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8B9690]">ft</span>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="11.9"
                    step="0.5"
                    data-testid="height-in-input"
                    className="ff-input pr-8"
                    value={hIn}
                    onChange={(e) => setHIn(e.target.value)}
                    placeholder="5"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8B9690]">in</span>
                </div>
              </div>
            )}
          </Field>

          {/* Weight with unit toggle */}
          <Field
            label="Weight"
            unitToggle={
              <UnitToggle
                value={weightUnit}
                options={[{ id: "kg", label: "kg" }, { id: "lb", label: "lb" }, { id: "st", label: "st + lb" }]}
                onChange={switchWeight}
                testid="weight-unit"
              />
            }
          >
            {weightUnit === "kg" && (
              <input
                type="number"
                min="25"
                max="400"
                step="0.1"
                data-testid="weight-input"
                className="ff-input"
                value={wKg}
                onChange={(e) => setWKg(e.target.value)}
                placeholder="68"
              />
            )}
            {weightUnit === "lb" && (
              <div className="relative">
                <input
                  type="number"
                  min="55"
                  max="880"
                  step="0.1"
                  data-testid="weight-lb-input"
                  className="ff-input pr-8"
                  value={wLb}
                  onChange={(e) => setWLb(e.target.value)}
                  placeholder="150"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8B9690]">lb</span>
              </div>
            )}
            {weightUnit === "st" && (
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <input
                    type="number"
                    min="3"
                    max="60"
                    data-testid="weight-st-input"
                    className="ff-input pr-8"
                    value={wSt}
                    onChange={(e) => setWSt(e.target.value)}
                    placeholder="10"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8B9690]">st</span>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="13.9"
                    step="0.1"
                    data-testid="weight-stlb-input"
                    className="ff-input pr-8"
                    value={wStLb}
                    onChange={(e) => setWStLb(e.target.value)}
                    placeholder="10"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8B9690]">lb</span>
                </div>
              </div>
            )}
          </Field>

          <Field label="Activity level">
            <div className="flex flex-wrap gap-2" data-testid="activity-picker">
              {ACTIVITY.map((a) => (
                <button
                  key={a.id}
                  data-testid={`activity-${a.id}`}
                  onClick={() => set("activity", a.id)}
                  title={a.hint}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition ${form.activity === a.id ? "bg-[#3B5A3E] text-white border-[#3B5A3E]" : "bg-white text-[#2A332C] border-[#E5E2DA] hover:border-[#3B5A3E]"}`}
                >{a.label}</button>
              ))}
            </div>
          </Field>

          <Field label="Goal">
            <div className="space-y-2" data-testid="goal-picker">
              {GOAL.map((g) => (
                <div key={g.id}>
                  <button
                    data-testid={`goal-${g.id}`}
                    onClick={() => set("goal", g.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border transition text-left ${form.goal === g.id ? "border-[#3B5A3E] bg-[#F3F0EA]" : "border-[#E5E2DA] hover:border-[#3B5A3E]"}`}
                  >
                    <span className="text-xl">{g.emoji}</span>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{goalLabel(g, weightUnit)}</div>
                      <div className="text-xs text-[#8B9690]">{g.hint}</div>
                    </div>
                    {form.goal === g.id && <span className="text-[#3B5A3E]">✓</span>}
                  </button>
                  {g.id === "custom" && form.goal === "custom" && (
                    <CustomGoalInput
                      kgPerWeek={form.custom_kg_per_week ?? -0.4}
                      unit={weightUnit}
                      onChange={(kg) => set("custom_kg_per_week", kg)}
                    />
                  )}
                </div>
              ))}
            </div>
          </Field>

          <div className="flex gap-2 pt-2">
            <button onClick={calculate} data-testid="calculate-btn" className="ff-btn-ghost border border-[#E5E2DA]">
              Recalculate
            </button>
            <button onClick={saveTarget} disabled={saving} data-testid="save-target-btn" className="ff-btn-primary disabled:opacity-60">
              {saving ? "Saving…" : "Save as my target"}
            </button>
          </div>
        </section>

        <aside data-testid="result-panel">
          {result ? (
            <div className="ff-card p-6 md:p-8 sticky top-6">
              <div className="ff-label">Live result</div>
              <div className="mt-3 space-y-4">
                <Row label="BMR (resting burn)" value={`${result.bmr} kcal`} />
                <Row label="TDEE (maintain)" value={`${result.tdee} kcal`} />
                <Row label="Recommended intake" value={`${result.daily_kcal} kcal`} accent />
                <Row label="Expected weight change" value={weeklyChangeLabel(result.weekly_kg_change, weightUnit)} />
              </div>
              <p className="text-xs text-[#8B9690] mt-6">
                Based on the Mifflin–St Jeor equation. We floor weight-loss targets at 1,200 kcal (♀) / 1,500 kcal (♂) for safety. Speak to a GP or dietitian before making big changes.
              </p>
            </div>
          ) : (
            <div className="ff-card p-8 text-center text-[#5C6B61]">
              <div className="text-5xl mb-3">🎯</div>
              <p className="text-sm">Hit <strong>Recalculate</strong> to see your daily kcal number.</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function Field({ label, unitToggle, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="ff-label">{label}</div>
        {unitToggle}
      </div>
      {children}
    </div>
  );
}

function UnitToggle({ value, options, onChange, testid }) {
  return (
    <div className="inline-flex bg-[#F3F0EA] rounded-full p-0.5" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          data-testid={`${testid}-${o.id}`}
          className={`px-3 py-1 rounded-full text-xs font-medium transition ${
            value === o.id ? "bg-white shadow-sm text-[#3B5A3E]" : "text-[#5C6B61]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className={`flex items-center justify-between gap-3 ${accent ? "py-3 px-3 rounded-xl bg-[#E8F0E9]" : ""}`}>
      <div className={`text-sm ${accent ? "font-medium text-[#3B5A3E]" : "text-[#5C6B61]"}`}>{label}</div>
      <div className={`text-sm ${accent ? "font-semibold text-[#3B5A3E]" : "font-medium text-[#2A332C]"}`}>{value}</div>
    </div>
  );
}

// Custom goal input — toggle between Lose / Gain and accept a per-week value in
// the user's preferred unit. Stores kg internally for the API.
function CustomGoalInput({ kgPerWeek, unit, onChange }) {
  // direction: -1 = lose, +1 = gain
  const direction = kgPerWeek >= 0 ? 1 : -1;
  const absKg = Math.abs(kgPerWeek);
  // Display amount in the chosen unit (kg or lb — st mode also uses lb).
  const displayAmt = unit === "kg"
    ? (absKg ? String(absKg) : "")
    : (absKg ? String(Math.round(absKg * 2.20462 * 100) / 100) : "");

  const [raw, setRaw] = useState(displayAmt);
  useEffect(() => { setRaw(displayAmt); }, [unit, kgPerWeek]); // eslint-disable-line

  const maxLose = unit === "kg" ? 1.0 : 2.2;     // ~1 kg / ~2.2 lb / week
  const maxGain = unit === "kg" ? 0.5 : 1.1;
  const limit = direction === -1 ? maxLose : maxGain;

  const apply = (rawValue, dir) => {
    setRaw(rawValue);
    const v = parseFloat(rawValue);
    if (isNaN(v) || v < 0) return onChange(0);
    const capped = Math.min(v, dir === -1 ? maxLose : maxGain);
    const kg = unit === "kg" ? capped : Math.round((capped / 2.20462) * 100) / 100;
    onChange(dir === -1 ? -kg : kg);
  };

  const unitLabel = unit === "kg" ? "kg" : "lb";
  const over = parseFloat(raw) > limit;

  return (
    <div className="mt-2 ml-12 mr-1 p-4 rounded-2xl bg-[#FAF9F6] border border-[#E5E2DA]" data-testid="custom-goal-input">
      <div className="ff-label">I want to…</div>
      <div className="mt-2 inline-flex bg-white border border-[#E5E2DA] rounded-full p-1">
        <button
          type="button"
          onClick={() => apply(raw, -1)}
          data-testid="custom-dir-lose"
          className={`px-3 py-1 rounded-full text-xs font-medium transition ${direction === -1 ? "bg-[#3B5A3E] text-white" : "text-[#5C6B61]"}`}
        >Lose</button>
        <button
          type="button"
          onClick={() => apply(raw, 1)}
          data-testid="custom-dir-gain"
          className={`px-3 py-1 rounded-full text-xs font-medium transition ${direction === 1 ? "bg-[#3B5A3E] text-white" : "text-[#5C6B61]"}`}
        >Gain</button>
      </div>

      <div className="mt-3 relative">
        <input
          type="number"
          step="0.05"
          min="0"
          max={limit}
          inputMode="decimal"
          value={raw}
          onChange={(e) => apply(e.target.value, direction)}
          placeholder="0.4"
          className="ff-input pr-16"
          data-testid="custom-goal-amount"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8B9690]">
          {unitLabel} / week
        </span>
      </div>

      {over && (
        <p className="text-[11px] text-[#A33D26] mt-2">
          ⚠️ Capped to {limit} {unitLabel} / week — anything faster isn&apos;t safe long-term.
        </p>
      )}
      <p className="text-[11px] text-[#8B9690] mt-2 leading-relaxed">
        Tip: NHS recommends losing 0.5–1 kg (1–2 lb) per week. Going faster risks muscle loss.
      </p>
    </div>
  );
}

